import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { exportToPDF, exportToExcel } from '../services/exportService';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material';
import api from '../services/api';

function Productos() {
  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroStock, setFiltroStock] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [formData, setFormData] = useState({
    id: null,
    codigo: '',
    nombre: '',
    descripcion: '',
    categoria_id: '',
    marca: '',
    modelo: '',
    precio_compra: '',
    precio_venta: '',
    stock_minimo: 5,
    stock_actual: 0,
    ubicacion_almacen: '',
    imagen: '',
  });

  const [imagenFile, setImagenFile] = useState(null);

  useEffect(() => {
    cargarProductos();
    cargarCategorias();
  }, []);

  const cargarProductos = async () => {
    try {
      const response = await api.get('/productos');
      setProductos(response.data.data || []);
    } catch (error) {
      console.error('Error cargando productos:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error cargando productos: ' + (error.response?.data?.error || error.message), 
        severity: 'error' 
      });
    }
  };

  const cargarCategorias = async () => {
    setLoadingCategorias(true);
    try {
      console.log('🔍 Cargando categorías...');
      const response = await api.get('/categorias');
      console.log('✅ Respuesta completa:', response);
      console.log('✅ response.data:', response.data);
      
      // Manejar diferentes estructuras de respuesta
      let categoriasData = [];
      if (response.data && Array.isArray(response.data.data)) {
        categoriasData = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        categoriasData = response.data;
      } else if (Array.isArray(response.data)) {
        categoriasData = response.data;
      }
      
      console.log('📦 Categorías extraídas:', categoriasData);
      setCategorias(categoriasData);
      
      if (categoriasData.length === 0) {
        console.warn('⚠️ No se encontraron categorías en la respuesta');
      }
    } catch (error) {
      console.error('❌ Error cargando categorías:', error);
      console.error('❌ Detalle:', error.response?.data || error.message);
      setSnackbar({ 
        open: true, 
        message: 'Error cargando categorías: ' + (error.response?.data?.error || error.message), 
        severity: 'error' 
      });
      setCategorias([]);
    } finally {
      setLoadingCategorias(false);
    }
  };

  const handleOpenDialog = (producto = null) => {
    if (producto) {
      setEditando(true);
      setFormData({
        id: producto.id,
        codigo: producto.codigo,
        nombre: producto.nombre,
        descripcion: producto.descripcion || '',
        categoria_id: producto.categoria_id || '',
        marca: producto.marca || '',
        modelo: producto.modelo || '',
        precio_compra: producto.precio_compra,
        precio_venta: producto.precio_venta,
        stock_minimo: producto.stock_minimo,
        stock_actual: producto.stock_actual,
        ubicacion_almacen: producto.ubicacion_almacen || '',
        imagen: producto.imagen || '',
      });
      setImagenFile(null);
    } else {
      setEditando(false);
      setFormData({
        id: null,
        codigo: '',
        nombre: '',
        descripcion: '',
        categoria_id: '',
        marca: '',
        modelo: '',
        precio_compra: '',
        precio_venta: '',
        stock_minimo: 5,
        stock_actual: 0,
        ubicacion_almacen: '',
        imagen: '',
      });
      setImagenFile(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      const data = new FormData();
      data.append('codigo', formData.codigo);
      data.append('nombre', formData.nombre);
      data.append('descripcion', formData.descripcion || '');
      data.append('categoria_id', formData.categoria_id || '');
      data.append('marca', formData.marca || '');
      data.append('modelo', formData.modelo || '');
      data.append('precio_compra', formData.precio_compra || 0);
      data.append('precio_venta', formData.precio_venta || 0);
      data.append('stock_minimo', formData.stock_minimo || 5);
      data.append('stock_actual', formData.stock_actual || 0);
      data.append('ubicacion_almacen', formData.ubicacion_almacen || '');

      if (editando && formData.imagen) {
        data.append('imagen_actual', formData.imagen);
      }

      if (imagenFile) {
        data.append('imagen', imagenFile);
      }

      if (editando) {
        await api.put(`/productos/${formData.id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setSnackbar({ open: true, message: 'Producto actualizado correctamente', severity: 'success' });
      } else {
        await api.post('/productos', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setSnackbar({ open: true, message: 'Producto creado correctamente', severity: 'success' });
      }
      handleCloseDialog();
      cargarProductos();
    } catch (error) {
      setSnackbar({ open: true, message: 'Error: ' + error.message, severity: 'error' });
    }
  };

  const handleEliminar = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este producto?')) {
      try {
        await api.delete(`/productos/${id}`);
        setSnackbar({ open: true, message: 'Producto eliminado', severity: 'success' });
        cargarProductos();
      } catch (error) {
        setSnackbar({ open: true, message: 'Error al eliminar', severity: 'error' });
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Inventario de Productos
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={() => {
              const headers = ['Código', 'Nombre', 'Categoría', 'Marca', 'Stock', 'Precio Compra', 'Precio Venta', 'Ubicación'];
              const data = productos.map(p => [
                p.codigo, p.nombre, p.categoria_nombre, p.marca, p.stock_actual,
                `S/ ${parseFloat(p.precio_compra).toFixed(2)}`,
                `S/ ${parseFloat(p.precio_venta).toFixed(2)}`,
                p.ubicacion_almacen
              ]);
              exportToPDF('Reporte de Inventario - Productos', headers, data, `productos_${new Date().toISOString().split('T')[0]}`);
            }}
            sx={{ mr: 1, color: '#d32f2f', borderColor: '#d32f2f' }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExcelIcon />}
            onClick={() => {
              const data = productos.map(p => ({
                'Código': p.codigo,
                'Nombre': p.nombre,
                'Categoría': p.categoria_nombre,
                'Marca': p.marca,
                'Modelo': p.modelo,
                'Stock Actual': p.stock_actual,
                'Stock Mínimo': p.stock_minimo,
                'Precio Compra': p.precio_compra,
                'Precio Venta': p.precio_venta,
                'Ubicación': p.ubicacion_almacen,
                'Estado': p.estado
              }));
              exportToExcel(data, `productos_${new Date().toISOString().split('T')[0]}`, 'Productos');
            }}
            sx={{ mr: 1, color: '#388e3c', borderColor: '#388e3c' }}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ backgroundColor: '#1976d2' }}
          >
            Nuevo Producto
          </Button>
        </Box>
      </Box>

      {/* Filtros y búsqueda */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Buscar producto..."
              placeholder="Nombre o código"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label="Categoría"
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              disabled={loadingCategorias}
              SelectProps={{
                displayEmpty: true,
              }}
            >
              <MenuItem value="">
                <em>Todas</em>
              </MenuItem>
              {categorias.length > 0 ? (
                categorias.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id.toString()}>
                    {cat.nombre}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  {loadingCategorias ? 'Cargando...' : 'Sin categorías'}
                </MenuItem>
              )}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label="Estado de Stock"
              value={filtroStock}
              onChange={(e) => setFiltroStock(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="bajo">Stock Bajo</MenuItem>
              <MenuItem value="ok">Stock OK</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button 
              fullWidth 
              variant="outlined" 
              onClick={() => {
                setBusqueda('');
                setFiltroCategoria('');
                setFiltroStock('');
              }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nombre</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Categoría</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Marca</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Stock</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Precio Compra</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Precio Venta</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {productos
              .filter(p => {
                const coincideBusqueda = !busqueda || 
                  p.nombre.toLowerCase().includes(busqueda.toLowerCase()) || 
                  p.codigo.toLowerCase().includes(busqueda.toLowerCase());
                const coincideCategoria = !filtroCategoria || p.categoria_id === parseInt(filtroCategoria);
                const coincideStock = !filtroStock || 
                  (filtroStock === 'bajo' ? p.stock_actual <= p.stock_minimo : p.stock_actual > p.stock_minimo);
                return coincideBusqueda && coincideCategoria && coincideStock;
              })
              .map((producto) => (
                <TableRow key={producto.id} hover>
                  <TableCell>{producto.codigo}</TableCell>
                  <TableCell>{producto.nombre}</TableCell>
                  <TableCell>{producto.categoria_nombre}</TableCell>
                  <TableCell>{producto.marca}</TableCell>
                  <TableCell>
                    <Chip 
                      label={producto.stock_actual} 
                      color={producto.stock_actual <= producto.stock_minimo ? 'error' : 'success'} 
                      size="small"
                    />
                  </TableCell>
                  <TableCell>S/ {parseFloat(producto.precio_compra).toFixed(2)}</TableCell>
                  <TableCell>S/ {parseFloat(producto.precio_venta).toFixed(2)}</TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleOpenDialog(producto)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleEliminar(producto.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar producto */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#1976d2', fontWeight: 'bold' }}>
          {editando ? 'Editar Producto' : 'Nuevo Producto'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código"
                name="codigo"
                value={formData.codigo}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Categoría"
                name="categoria_id"
                value={formData.categoria_id}
                onChange={handleChange}
                disabled={loadingCategorias || categorias.length === 0}
              >
                <MenuItem value=""><em>Seleccione una categoría</em></MenuItem>
                {categorias.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Marca"
                name="marca"
                value={formData.marca}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Modelo"
                name="modelo"
                value={formData.modelo}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ubicación en Almacén"
                name="ubicacion_almacen"
                value={formData.ubicacion_almacen}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Precio Compra (S/)"
                name="precio_compra"
                type="number"
                value={formData.precio_compra}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Precio Venta (S/)"
                name="precio_venta"
                type="number"
                value={formData.precio_venta}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Stock Mínimo"
                name="stock_minimo"
                type="number"
                value={formData.stock_minimo}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Stock Actual"
                name="stock_actual"
                type="number"
                value={formData.stock_actual}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="file"
                label="Imagen del Producto"
                name="imagenFile"
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "image/*" }}
                onChange={(e) => setImagenFile(e.target.files[0])}
              />
            </Grid>
            {formData.imagen && (
              <Grid item xs={12} md={6}>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Imagen actual:</Typography>
                  <img 
                    src={`http://localhost:5000${formData.imagen}`} 
                    alt="Producto" 
                    style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, marginTop: 8 }} 
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ backgroundColor: '#1976d2' }}>
            {editando ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensajes */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Productos;